import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, IconButton, Collapse, Tooltip, Grid } from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  LocationOn,
  Refresh,
  MoreVert,
  WarningAmber,
  NotificationsActive,
  InfoOutlined
} from '@mui/icons-material';
import { limitToLast, onChildAdded, query, ref } from 'firebase/database';
import LiveTrackingMap, { ParticipantInfo, AddressLocation } from '../TrackingMap';
import TransactionAlertsSection, { TransactionAlert, parseAlertsResponse } from '../TransactionAlertsSection';
import StatusPill from './StatusPill';
import ApiContext from 'contexts/ApiContext';
import useFirebase from 'hooks/useFirebase';

export type DriverStatusKind = 'ON_ROUTE' | 'DELAYED' | 'ROUTE_DEVIATION' | 'COMPLETED' | 'UNKNOWN' | 'ASSIGNED';

interface DriverStatus {
  uid: string;
  name: string;
  status: DriverStatusKind;
}

// This panel owns the alert fetch for the whole card: the header worst-state
// chip derives from it, and the alerts section is handed the same array instead
// of requesting it a second time. Per-driver trip status + presence now live in
// the Assigned Driver Info panel, which consumes the same alert feed.
type Alert = TransactionAlert;

export interface DriverEventPoint {
  /** Display name of the user who created the LOCATION event. */
  name: string;
  /** Optional role / team identifier rendered in parens after the name. */
  role?: string;
  /** Formatted address from the location reverse-geocode (preferred). */
  locationText?: string;
  /** Raw GPS coordinates [lat, lng] — shown if no address is available. */
  coordinates?: [number, number];
  /** Pre-formatted timestamp the LOCATION event was created at. */
  createdAt?: string;
}

export interface DriverSummary {
  /** Display name (used in the chip label). */
  name: string;
  /** Optional role rendered next to the chip. */
  role?: string;
  /** Start event — the earliest LOCATION event the driver submitted. */
  start?: DriverEventPoint;
  /** End event — the latest LOCATION event the driver submitted. */
  end?: DriverEventPoint;
}

/** @deprecated kept for source-compat; prefer DriverSummary. */
export type DriverStartSummary = DriverEventPoint;

interface Props {
  transactionUid: string;
  teamId?: string;
  teamUid?: string;
  participants: ParticipantInfo[];
  /** Uids that are actually being live-tracked. Kept for source-compat with the
   * details page; per-driver status/presence is now rendered by the Assigned
   * Driver Info panel rather than a row under the map. */
  trackedUids?: string[];
  /**
   * Per-driver Start + End metadata surfaced in the panel header. Each entry
   * gets a compact chip with a hover-only info icon that opens a rich
   * tooltip listing CREATED BY / time / location for both start and end.
   */
  drivers?: DriverSummary[];
  /** Pickup / destination addresses (with coordinates) plotted on the map. */
  pickup?: AddressLocation | null;
  destination?: AddressLocation | null;
}

const STATUS_WINDOW_MS = 10 * 60 * 1000;

/**
 * Top-row Live Driver Tracking card. Renders the existing <LiveTrackingMap>
 * (unchanged: still reads locations/{txnUid} via onValue and renders every
 * driver's polyline) plus the Start/End info chips above it and the inline
 * alerts column beside it.
 *
 * The per-driver status row that used to sit under the map has been removed:
 * name, live trip status (On Route / Delayed / Route Deviation) and
 * online/offline presence are now consolidated into the "Assigned Driver Info"
 * panel so the same details aren't shown twice. The header still surfaces a
 * worst-state chip (Route Deviation / Delayed) derived from recent alerts so
 * it's visible even before the user expands the panel.
 */
const LiveDriverPanel: React.FC<Props> = ({ transactionUid, teamId, teamUid, participants, drivers: driverSummaries = [], pickup, destination }) => {
  const apiContext = useContext(ApiContext);
  const { database, firebaseUid } = useFirebase();
  const [drivers, setDrivers] = useState<DriverStatus[]>([]);
  const [expanded, setExpanded] = useState(true);
  const [updatedLabel, setUpdatedLabel] = useState<string>('');
  const [rawAlerts, setRawAlerts] = useState<Alert[]>([]);
  const [ruleTotals, setRuleTotals] = useState<Record<string, { total: number }>>({});

  const inFlightRef = useRef(false);
  // Per-participant last app-login (only app-permitted user resources are
  // returned). Used to fill map names for tracked drivers that submitted no
  // events, so a name shows instead of a raw uid.
  const [lastLoginMap, setLastLoginMap] = useState<Record<string, { name?: string }>>({});

  const enrichedParticipants = useMemo(
    () => participants.map((p) => ({ ...p, name: p.name || lastLoginMap[p.uid]?.name || '' })),
    [participants, lastLoginMap]
  );
  const participantUidsKey = participants
    .map((p) => p.uid)
    .filter(Boolean)
    .join(',');

  // Fetch last app-login for the drivers shown on the map (best-effort, name only).
  useEffect(() => {
    if (!apiContext || !participantUidsKey) return;
    let cancelled = false;
    const uids = participantUidsKey.split(',');
    (async () => {
      try {
        const res: any = await (apiContext as any).getLastLoginBatch?.(uids);
        const data = res?.data ?? {};
        if (!cancelled) setLastLoginMap(data && typeof data === 'object' ? data : {});
      } catch {
        // best-effort — leave names off
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiContext, participantUidsKey]);

  const computeStatus = useCallback((alertsForUser: Alert[]): DriverStatusKind => {
    if (!alertsForUser.length) return 'ON_ROUTE';
    const latest = alertsForUser
      .filter((a) => {
        if (!a.firedAt) return false;
        return Date.now() - new Date(a.firedAt).getTime() < STATUS_WINDOW_MS;
      })
      .sort((a, b) => new Date(b.firedAt!).getTime() - new Date(a.firedAt!).getTime())[0];
    if (!latest) return 'ON_ROUTE';
    if (latest.ruleType === 'GEO_FENCE') return 'ROUTE_DEVIATION';
    if (latest.ruleType === 'STEP_TIMER' || latest.ruleType === 'INTER_STEP_TRACKING') {
      return 'DELAYED';
    }
    return 'ON_ROUTE';
  }, []);

  const refreshStatuses = useCallback(async () => {
    if (!apiContext || !transactionUid || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res: any = await (apiContext as any).getTransactionAlerts?.(transactionUid);
      const parsed = parseAlertsResponse(res);
      const alerts: Alert[] = parsed.alerts;
      setRawAlerts(alerts);
      setRuleTotals(parsed.ruleTotals);

      const byUser = new Map<string, Alert[]>();
      for (const a of alerts) {
        const uid = a.context?.userId;
        if (!uid) continue;
        const arr = byUser.get(uid) || [];
        arr.push(a);
        byUser.set(uid, arr);
      }

      const next: DriverStatus[] = participants.map((p) => ({
        uid: p.uid,
        name: p.name || '',
        status: computeStatus(byUser.get(p.uid) || [])
      }));
      setDrivers(next);
      setUpdatedLabel('just now');
    } catch {
      setDrivers(
        participants.map((p) => ({
          uid: p.uid,
          name: p.name || '',
          status: 'UNKNOWN'
        }))
      );
    } finally {
      inFlightRef.current = false;
    }
  }, [apiContext, transactionUid, participants, computeStatus]);

  useEffect(() => {
    refreshStatuses();
  }, [refreshStatuses]);

  useEffect(() => {
    if (!database || !firebaseUid || (!teamId && !teamUid)) return;
    const candidates = [
      teamId ? `transaction-refresh/team/${teamId}` : null,
      teamUid ? `transaction-refresh/team/${teamUid}` : null,
      teamId ? `transaction-refresh/${teamId}` : null,
      teamUid ? `transaction-refresh/${teamUid}` : null
    ].filter(Boolean) as string[];
    const unsubs = candidates.map((path) => {
      // Bound the initial sync (accumulating transaction-refresh events).
      const r = query(ref(database, path), limitToLast(20));
      return onChildAdded(r, (snap) => {
        const v: any = snap.val();
        if (!v || v.action !== 'ALERT_FIRED') return;
        const isThisTxn = v.keyUid === transactionUid || v.transactionUid === transactionUid || v?.meta?.transactionUid === transactionUid;
        if (isThisTxn) refreshStatuses();
      });
    });
    return () => unsubs.forEach((fn) => fn && fn());
  }, [database, firebaseUid, teamId, teamUid, transactionUid, refreshStatuses]);

  // Worst-state pill displayed in the header.
  const headerChip = useMemo(() => {
    if (drivers.some((d) => d.status === 'ROUTE_DEVIATION')) {
      return <StatusPill kind="warning" icon={<WarningAmber sx={{ fontSize: 14 }} />} label="Route Deviation" />;
    }
    if (drivers.some((d) => d.status === 'DELAYED')) {
      return <StatusPill kind="warning" icon={<WarningAmber sx={{ fontSize: 14 }} />} label="Delayed" />;
    }
    return null;
  }, [drivers]);

  if (!transactionUid) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden'
      }}
    >
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          bgcolor: 'primary.dark',
          color: '#fff',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <LocationOn sx={{ color: '#fff', fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#fff' }}>
            Live Driver Tracking
          </Typography>
          {headerChip}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          {updatedLabel && (
            <Typography variant="caption" sx={{ mr: 0.5, color: 'rgba(255,255,255,0.75)' }}>
              Updated {updatedLabel}
            </Typography>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refreshStatuses} sx={{ color: '#fff' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" sx={{ color: '#fff' }}>
            <MoreVert fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Compact driver row — one chip per driver with a hover-only info
          icon that opens a rich tooltip listing both Start and End details
          (CREATED BY / time / location). Keeps the header dense; the full
          details are only revealed on demand. */}
      {expanded && driverSummaries.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          {driverSummaries.map((d, idx) => (
            <DriverHeaderChip key={idx} driver={d} />
          ))}
        </Box>
      )}

      <Collapse in={expanded}>
        <Grid container>
          {/* Left: map */}
          <Grid item xs={12} md={8} sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}>
            <LiveTrackingMap txnId={transactionUid} participants={enrichedParticipants} pickup={pickup} destination={destination} />
          </Grid>
          {/* Right: alerts embedded inline */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
              }}
            >
              <NotificationsActive sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Info and Alerts
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: { md: 300 },
                maxHeight: { md: 360 },
                overflowY: 'auto',
                px: 2,
                py: 1.5
              }}
            >
              {/* Alerts are passed down rather than re-fetched: this panel already
                  loads them for the header status chip, and both components
                  refreshing on every ALERT_FIRED meant two identical requests. */}
              <TransactionAlertsSection
                transactionUid={transactionUid}
                teamId={teamId}
                teamUid={teamUid}
                embedded
                alerts={rawAlerts}
                ruleTotals={ruleTotals}
              />
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Per-driver chip with hover info tooltip
// ---------------------------------------------------------------------------

const DriverHeaderChip: React.FC<{ driver: DriverSummary }> = ({ driver }) => {
  const hasDetails = Boolean(driver.start || driver.end);
  const chip = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50'
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'primary.dark' }}>
        {driver.name}
      </Typography>
      {driver.role && (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
          ({driver.role})
        </Typography>
      )}
      {hasDetails && <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />}
    </Box>
  );

  if (!hasDetails) return chip;

  return (
    <Tooltip
      arrow
      placement="bottom-start"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            maxWidth: 360,
            p: 0
          }
        },
        arrow: { sx: { color: 'background.paper' } }
      }}
      title={<DriverDetailsCard driver={driver} />}
    >
      {chip}
    </Tooltip>
  );
};

const DriverDetailsCard: React.FC<{ driver: DriverSummary }> = ({ driver }) => {
  return (
    <Box sx={{ p: 1.5, minWidth: 260 }}>
      {driver.start && <DriverEventBlock heading="Started" point={driver.start} />}
      {driver.start && driver.end && <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 1.5 }} />}
      {driver.end && <DriverEventBlock heading="Ended" point={driver.end} />}
    </Box>
  );
};

const DriverEventBlock: React.FC<{ heading: 'Started' | 'Ended'; point: DriverEventPoint }> = ({ heading, point }) => {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'primary.dark',
          display: 'block',
          mb: 0.5
        }}
      >
        {heading} By:{' '}
        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {point.name}
        </Typography>
        {point.role && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 500 }}>
            ({point.role})
          </Typography>
        )}
      </Typography>
      {point.createdAt && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
          {heading === 'Started' ? 'Created' : 'Ended'}: <span style={{ color: 'inherit', fontWeight: 500 }}>{point.createdAt}</span>
        </Typography>
      )}
      {(point.locationText || point.coordinates) && (
        <Box sx={{ mt: 0.75 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'primary.dark',
              display: 'block',
              mb: 0.5
            }}
          >
            Location
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <LocationOn sx={{ fontSize: 18, color: 'error.main', mt: 0.25 }} />
            <Box sx={{ minWidth: 0 }}>
              {point.locationText && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', wordBreak: 'break-word' }}>
                  {point.locationText}
                </Typography>
              )}
              {point.coordinates && (
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
                  {point.coordinates[0].toFixed(4)}, {point.coordinates[1].toFixed(4)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LiveDriverPanel;
